import { UseQueryResult } from '@tanstack/react-query';
import {
  Event,
  EventConfigScoreOverride,
  SellerAccountEventConfigScoreOverrides,
  TicketClassInfo,
  VenueMapInfo,
  VenueZoneConfigOverride,
} from 'src/WebApiController';
import { useContextSelector } from 'use-context-selector';

import { ErrorTypes } from '../ClientLogContext';
import {
  EventMapContextProps,
  EventMapContextWithSelector,
} from './EventMapContext';

const useEventMapContextValueSelector = <T>(
  selector: (context: EventMapContextProps) => T
): T => {
  const contextValue = useContextSelector(
    EventMapContextWithSelector,
    selector
  );

  return contextValue;
};

export const useEventMapContextEventSelector = (): Event | null | undefined => {
  return useEventMapContextValueSelector((context) => context.event);
};

export const useEventMapContextTicketClassesSelector =
  (): TicketClassInfo[] => {
    return useEventMapContextValueSelector((context) => context.ticketClasses);
  };

export const useEventMapContextVenueMapQuerySelector = (): UseQueryResult<
  VenueMapInfo | null,
  ErrorTypes
> => {
  return useEventMapContextValueSelector((context) => context.venueMapQuery);
};

export const useEventMapContextVenueMapsByScoreModelQuerySelector =
  (): UseQueryResult<
    {
      readonly VenueGeometry?: VenueMapInfo | undefined;
      readonly MergedVenueGeometry?: VenueMapInfo | undefined;
      readonly AdvancedVenueGeometry?: VenueMapInfo | undefined;
    } | null,
    ErrorTypes
  > => {
    return useEventMapContextValueSelector(
      (context) => context.venueMapsByScoreModelQuery
    );
  };

export const useEventMapContextIsMapLoadingSelector = (): boolean => {
  return useEventMapContextValueSelector((context) => context.isMapLoading);
};

export const useEventMapContextVenueMapInfoSelector =
  (): VenueMapInfo | null => {
    return useEventMapContextValueSelector((context) => context.venueMapInfo);
  };

export const useEventMapContextMapConfigOverridesQuerySelector =
  (): UseQueryResult<EventConfigScoreOverride[] | null, ErrorTypes> => {
    return useEventMapContextValueSelector(
      (context) => context.mapConfigOverridesQuery
    );
  };

export const useEventMapContextSellerAccountScoreOverridesQuerySelector =
  (): UseQueryResult<
    SellerAccountEventConfigScoreOverrides[] | null,
    ErrorTypes
  > => {
    return useEventMapContextValueSelector(
      (context) => context.sellerAccountScoreOverridesQuery
    );
  };

export const useEventMapContextActiveConfigOverrideSelector = ():
  | EventConfigScoreOverride
  | null
  | undefined => {
  return useEventMapContextValueSelector(
    (context) => context.activeConfigOverride
  );
};

export const useEventMapContextIsGeneralAdmissionOnlySelector = (): boolean => {
  return useEventMapContextValueSelector(
    (context) => context.isGeneralAdmissionOnly
  );
};

export const useEventMapContextNormalizeConfigOverrideWithUiScoreSelector =
  (): ((
    scoreOverride: EventConfigScoreOverride
  ) => EventConfigScoreOverride) => {
    return useEventMapContextValueSelector(
      (context) => context.normalizeConfigOverrideWithUiScore
    );
  };

export const useEventMapContextVenueZoneConfigsSelector =
  (): VenueZoneConfigOverride | null => {
    return useEventMapContextValueSelector(
      (context) => context.venueZoneConfigs
    );
  };
